/**
 * Blog Article Enhancements
 * 
 * Provides enhanced functionality for blog articles:
 * - Product card generation from bracketed URLs
 * - Blog card generation from bracketed URLs
 * - Table of contents generation from H2 headings
 */

(() => {
  const origin = document.querySelector('.blog-post-content');
  if (!origin) return;

  /**
   * Replace paragraph URLs with product/blog cards
   */
  {
    const regex = /\[(https:\/\/.*)\]/;
    const domParser = new DOMParser();

    const targets = [...origin.querySelectorAll('p')].filter((el) =>
      regex.test(el.textContent)
    );

    const replaceParagraphToProductLink = async (snippet) => {
      const match = snippet.textContent.match(regex);
      if (!match) return;

      const [, url] = match;
      if (!url) return;

      // Handle blog URLs
      if (url.includes('blogs')) {
        try {
          const response = await fetch(url);
          const rawHTML = await response.text();
          const parsed = domParser.parseFromString(rawHTML, 'text/html');

          const titleElement = parsed.querySelector('meta[property="og:title"]');
          const title = titleElement && titleElement.getAttribute('content');

          const descriptionElement = parsed.querySelector(
            'meta[property="og:description"]'
          );
          const description =
            descriptionElement && descriptionElement.getAttribute('content');

          const imageElement = parsed.querySelector('meta[property^="og:image"]');
          let image = imageElement && imageElement.getAttribute('content');

          // Resize image to 240x240
          if (image) {
            if (/_\d{2,4}x\d{2,4}/.test(image)) {
              image = image.replace(/_\d{2,4}x\d{2,4}/, '_240x240');
            } else {
              image = image.replace(/(\.(jpg|jpeg|png|webp|gif))(\?.*)?$/, '_240x240$1');
            }
          }

          snippet.insertAdjacentHTML(
            'afterend',
            `
            <a class="blogcard" href="${url}">
              ${
                image
                  ? `<figure class="blogcard__image"><img src="${image}" alt="${title}" style="width:100%;height:100%;object-fit:cover;"></figure>`
                  : ''
              }
              <div class="blogcard__content">
                <div class="blogcard__title">${title}</div>
                <div class="blogcard__description">${description}</div>
                <div class="blogcard__url">${url}</div>
              </div>
            </a>
          `
          );
        } catch (error) {
          console.error('Blog card generation error:', error);
        } finally {
          snippet.parentNode.removeChild(snippet);
        }
        return;
      }

      // Handle product URLs
      if (url.includes('products')) {
        try {
          const response = await fetch(url);
          const rawHTML = await response.text();
          const parsed = domParser.parseFromString(rawHTML, 'text/html');

          let mainPrice, subPrice;

          const priceElements = [
            ...parsed.querySelectorAll('.selling-plan-select__price i'),
          ];

          if (priceElements.length > 0) {
            mainPrice =
              priceElements[0] &&
              priceElements[0].textContent.replace(/(\(税込|\))/g, '');
            subPrice =
              priceElements[1] &&
              priceElements[1].textContent.replace(/(\(税込|\))/g, '');
          } else {
            const priceElement = parsed.querySelector('.product__price');
            mainPrice = priceElement && priceElement.textContent;
          }

          const titleElement = parsed.querySelector('meta[property="og:title"]');
          const title = titleElement && titleElement.getAttribute('content');

          const descriptionElement = parsed.querySelector(
            'meta[property="og:description"]'
          );
          const description =
            descriptionElement && descriptionElement.getAttribute('content');

          const imageElement = parsed.querySelector('meta[property^="og:image"]');
          const image = imageElement && imageElement.getAttribute('content');

          const buttonText = (() => {
            const regex2 = new RegExp(`^${window.location.origin}/`);
            const sameDomain = regex2.test(url);
            if (!sameDomain) return 'リンク先はこちら';
            const splitted = url.replace(regex2, '').split('/');
            const category = splitted[0];
            switch (category) {
              case 'products':
                return '商品を見る';
              case 'collection':
                return '商品一覧を見る';
              case 'blogs':
                return splitted.length > 2 ? '記事を見る' : '記事一覧を見る';
              default:
                return 'リンク先はこちら';
            }
          })();

          snippet.insertAdjacentHTML(
            'afterend',
            `
            <a class="blogcard" href="${url}">
              ${
                image
                  ? `
              <figure class="blogcard__image">
                <img src="${image}" alt="${title}">
              </figure>
            `
                  : ''
              }
              <div class="blogcard__content">
                <div class="blogcard__title">${title}</div>
                ${
                  mainPrice || subPrice
                    ? `<div class="blogcard__price">`
                    : ''
                }
                  ${subPrice ? `<span class="strike">${subPrice}</span>` : ''}
                  ${mainPrice ? `<span class="bold">${mainPrice}</span>` : ''}
                ${mainPrice || subPrice ? `</div>` : ''}
                <div class="blogcard__description">${description}</div>
              </div>
            </a>
          `
          );
        } catch (error) {
          console.error('Product card generation error:', error);
        } finally {
          snippet.parentNode.removeChild(snippet);
        }
        return;
      }

      // Other URLs - do nothing
      return;
    };

    Promise.all(targets.map((el) => replaceParagraphToProductLink(el)));
  }

  /**
   * Generate table of contents from H2 headings
   */
  {
    const titles = [...origin.querySelectorAll('h2')];
    if (titles.length < 1) return;

    const listHtml = titles.reduce((result, current, index) => {
      current.id = `toc-${index + 1}`;

      const text = current.innerText.replace(/(\\n)/g, '');
      if (text !== '') {
        result += `
          <li class="toc__list-item">
            <a href="#toc-${index + 1}" data-consider-header="true">
            ${text}
            </a>
          </li>
        `;
      }

      return result;
    }, '');

    const tocHtml = `
    <div class="toc">
      <p class="toc__title"><span>目次</span></p>
      <ul class="toc__list">${listHtml}</ul>
    </div>
    `;

    titles[0].insertAdjacentHTML('beforebegin', tocHtml);
  }
})();
